import React, { useEffect, useId, useRef, useState } from 'react';
import {
  buildAddAdminProspectNoteRequest,
  buildPatchAdminProspectFollowUpRequest,
} from '../../services/admin/prospectsListRequest';
import { addAdminProspectNote, updateAdminProspect } from '../../services/admin/prospectsService';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectListErrorKey,
  adminProspectNoteErrorKey,
  adminProspectStatusLabel,
  classifyAdminCallableError,
  formatAdminDate,
} from './adminProspectDisplay';
import {
  adminProspectFollowUpTimeZoneLabel,
  adminProspectQuickFollowUpStatusOptions,
  parseAdminProspectDatetimeLocal,
} from './adminProspectQuickFollowUp';
import {
  beginQuickFollowUpNoteWrite,
  buildFollowUpPatchFields,
  captureQuickFollowUpSaveRequest,
  clearQuickFollowUpRefreshFailure,
  createQuickFollowUpFormState,
  dismissQuickFollowUpReview,
  enterQuickFollowUpConflictReview,
  failQuickFollowUpSave,
  markQuickFollowUpNoteWriteError,
  markQuickFollowUpNoteWriteSuccess,
  markQuickFollowUpRefreshFailure,
  reconcileQuickFollowUpControl,
  runQuickFollowUpControlSave,
  setQuickFollowUpDraft,
  shouldApplyQuickFollowUpLiveResult,
  syncQuickFollowUpFormFromProspect,
} from './adminProspectQuickFollowUpController';
import {
  applyWorkspaceConflictReloadToForm,
  runWorkspaceEditorRefreshRetry,
} from './adminProspectFollowUpWorkspace.js';

/**
 * Compact quick follow-up actions for workspace rows.
 * Reuses adminProspectQuickFollowUpController — does not duplicate the async state machine.
 */
export default function AdminProspectFollowUpWorkspaceQuickActions({
  prospect,
  language,
  t,
  onSaved,
  onClose,
  initiallyExpanded = true,
}) {
  const statusId = useId();
  const followUpId = useId();
  const noteId = useId();
  const mountedRef = useRef(true);
  const refreshRetryGenRef = useRef(0);
  const formStateRef = useRef(null);
  const prospectRef = useRef(prospect);
  const [formState, setFormState] = useState(() => createQuickFollowUpFormState(prospect));
  const [expanded, setExpanded] = useState(Boolean(initiallyExpanded));
  const [refreshRetryBusy, setRefreshRetryBusy] = useState(false);

  formStateRef.current = formState;
  prospectRef.current = prospect;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Invalidate in-flight read-only retries on unmount / close.
      refreshRetryGenRef.current += 1;
    };
  }, []);

  useEffect(() => {
    // Prospect switch or reopen (A→B→A): invalidate pending retries.
    refreshRetryGenRef.current += 1;
    setRefreshRetryBusy(false);
  }, [prospect?.id]);

  useEffect(() => {
    setFormState((current) => syncQuickFollowUpFormFromProspect(current, prospect));
  }, [
    prospect?.id,
    prospect?.status,
    prospect?.nextFollowUpAt,
    prospect?.lastContactAt,
    prospect?.updatedAt,
    prospect?.updatedAtVersion,
  ]);

  const statusReview = formState.review?.status?.conflict;
  const followUpReview = formState.review?.nextFollowUpAt?.conflict;
  const hasActionableIssue =
    Boolean(statusReview || followUpReview) ||
    Boolean(formState.controlUi?.status?.errorKey) ||
    Boolean(formState.controlUi?.nextFollowUpAt?.errorKey) ||
    Boolean(formState.note?.errorKey) ||
    Boolean(formState.refreshAfterWrite);

  useEffect(() => {
    if (hasActionableIssue && !expanded) {
      setExpanded(true);
    }
  }, [hasActionableIssue, expanded]);

  if (!prospect?.id) return null;

  const ownerProspectId = String(prospect.id).trim();
  const visitId = formState.visitId || 1;

  const mayApplyLive = (requestVisitId = visitId) =>
    shouldApplyQuickFollowUpLiveResult({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId: () => ownerProspectId,
      getActiveVisitId: () => visitId,
      getMounted: () => mountedRef.current,
    });

  const anyCrmBusy =
    formState.controlUi.status.saving ||
    formState.controlUi.nextFollowUpAt.saving ||
    formState.note.status === 'pending' ||
    refreshRetryBusy;

  const applyRefreshResult = (control, refresh, requestVisitId) => {
    if (!mayApplyLive(requestVisitId)) return;
    if (refresh?.refreshOk === false && refresh?.applied) {
      setFormState((current) => markQuickFollowUpRefreshFailure(current, control));
      setExpanded(true);
    } else if (refresh?.refreshOk) {
      setFormState((current) => clearQuickFollowUpRefreshFailure(current));
    }
  };

  const handleRefreshRetry = async () => {
    const control = formState.refreshAfterWrite?.control;
    if (!control || refreshRetryBusy || typeof onSaved !== 'function') return;
    const requestVisitId = visitId;
    const retryGen = refreshRetryGenRef.current;
    setRefreshRetryBusy(true);
    try {
      // Read-only recovery — never replays note/status/date writes.
      // Apply outcome to the *latest* form via functional updater (no pre-await snapshot install).
      const result = await runWorkspaceEditorRefreshRetry({
        formState: formStateRef.current,
        getAuthoritativeProspect: () => prospectRef.current,
        applyToForm: (updater) => setFormState(updater),
        requestVisitId,
        mayApply: mayApplyLive,
        onRefresh: async ({ conflictReload = false, refreshOnly = false } = {}) =>
          onSaved({
            prospectId: ownerProspectId,
            conflictReload,
            refreshOnly,
          }),
      });
      if (retryGen !== refreshRetryGenRef.current) return;
      if (result.abandoned) return;
      if (!result.ok || formStateRef.current?.refreshAfterWrite) {
        setExpanded(true);
      }
    } finally {
      if (retryGen === refreshRetryGenRef.current) {
        setRefreshRetryBusy(false);
      }
    }
  };

  const saveControl = async (control, { fieldsOverride } = {}) => {
    if (formState.controlUi[control]?.saving || anyCrmBusy) return;
    if (formState.review?.[control]?.conflict) return;

    if (control === 'status') {
      const next = formState.drafts.status;
      if (next === prospect.status) {
        setFormState((current) =>
          reconcileQuickFollowUpControl(current, 'status', next, current.baselines.versionToken),
        );
        return;
      }
      if (next === 'converted' && prospect.status !== 'converted') {
        setFormState((current) =>
          failQuickFollowUpSave(current, 'status', {
            errorKey: 'admin.prospection.quickFollowUp.status.convertedBlocked',
          }),
        );
        return;
      }
    }

    const built = fieldsOverride
      ? { ok: true, fields: fieldsOverride }
      : buildFollowUpPatchFields(control, formState);
    if (!built.ok) {
      setFormState((current) =>
        failQuickFollowUpSave(current, control, { errorKey: built.errorKey }),
      );
      return;
    }

    if (control === 'nextFollowUpAt' && !fieldsOverride) {
      const parsed = parseAdminProspectDatetimeLocal(formState.drafts.nextFollowUpAtLocal);
      if (parsed.empty) {
        if (!prospect.nextFollowUpAt && !formState.drafts.nextFollowUpAtLocal.trim()) {
          setFormState((current) =>
            reconcileQuickFollowUpControl(
              current,
              'nextFollowUpAt',
              null,
              current.baselines.versionToken,
            ),
          );
          return;
        }
        if (prospect.nextFollowUpAt) {
          setFormState((current) =>
            failQuickFollowUpSave(current, 'nextFollowUpAt', {
              errorKey: 'admin.prospection.quickFollowUp.followUp.clearRequired',
            }),
          );
          return;
        }
      }
      if (parsed.ok && parsed.iso === (prospect.nextFollowUpAt || null)) {
        setFormState((current) =>
          reconcileQuickFollowUpControl(
            current,
            'nextFollowUpAt',
            parsed.iso,
            current.baselines.versionToken,
          ),
        );
        return;
      }
    }

    if (!fieldsOverride && !formState.dirty[control]) {
      const savedValue =
        control === 'status' ? formState.drafts.status : prospect.nextFollowUpAt;
      setFormState((current) =>
        reconcileQuickFollowUpControl(current, control, savedValue, current.baselines.versionToken),
      );
      return;
    }

    const requestVisitId = visitId;
    const savedValue =
      control === 'status' ? built.fields.status : built.fields.nextFollowUpAt;
    const request = captureQuickFollowUpSaveRequest(formState, control, {
      fields: built.fields,
      savedValue,
    });

    const saveResult = await runQuickFollowUpControlSave({
      applyState: (updater) => setFormState(updater),
      mayApply: mayApplyLive,
      request,
      requestVisitId,
      classifyError: classifyAdminCallableError,
      write: async ({ fields, versionToken }) => {
        await updateAdminProspect(
          buildPatchAdminProspectFollowUpRequest(ownerProspectId, fields, versionToken),
        );
      },
      onConflict: async () => {
        // Provisional conflict from the known prop — do not treat as a fresh reload.
        setFormState((current) => enterQuickFollowUpConflictReview(current, control, prospect));
        setExpanded(true);
        if (typeof onSaved !== 'function') return;
        const refresh = await onSaved({ prospectId: ownerProspectId, conflictReload: true });
        if (!mayApplyLive(requestVisitId)) return;
        setFormState((current) => {
          const applied = applyWorkspaceConflictReloadToForm({
            formState: current,
            control,
            refresh,
            // Authoritative editor snapshot from parent — not only the dirty form baseline.
            referenceProspect: prospectRef.current,
          });
          return applied.formState;
        });
        setExpanded(true);
      },
      onError: async (_err, kind) => {
        setFormState((current) =>
          failQuickFollowUpSave(current, control, {
            errorKey: adminProspectListErrorKey(kind),
            errorKind: kind,
          }),
        );
        setExpanded(true);
      },
    });

    if (!saveResult.ok || saveResult.stale || saveResult.conflict) return;
    if (typeof onSaved === 'function') {
      const refresh = await onSaved({ prospectId: ownerProspectId, control });
      applyRefreshResult(control, refresh, requestVisitId);
    }
  };

  const handleClearFollowUp = async () => {
    if (formState.controlUi.nextFollowUpAt.saving || anyCrmBusy) return;
    if (!prospect.nextFollowUpAt && !formState.drafts.nextFollowUpAtLocal.trim()) {
      setFormState((current) =>
        reconcileQuickFollowUpControl(current, 'nextFollowUpAt', null, current.baselines.versionToken),
      );
      return;
    }
    await saveControl('nextFollowUpAt', { fieldsOverride: { nextFollowUpAt: null } });
  };

  const saveNote = async () => {
    const body = String(formState.note?.body || '').trim();
    if (!body || formState.note.status === 'pending') return;
    const next = beginQuickFollowUpNoteWrite(formState);
    const noteRevision = next.note.revision;
    setFormState(next);
    try {
      await addAdminProspectNote(buildAddAdminProspectNoteRequest({ prospectId: ownerProspectId, body }));
      if (!mayApplyLive()) return;
      setFormState((current) => markQuickFollowUpNoteWriteSuccess(current, { noteRevision }));
      if (typeof onSaved === 'function') {
        const refresh = await onSaved({ prospectId: ownerProspectId, note: true });
        applyRefreshResult('status', refresh, visitId);
      }
    } catch (err) {
      if (!mayApplyLive()) return;
      const kind = classifyAdminCallableError(err);
      setFormState((current) =>
        markQuickFollowUpNoteWriteError(current, {
          noteRevision,
          errorKey: adminProspectNoteErrorKey(kind),
          uncertainDuplicateRisk: kind === 'unavailable' || kind === 'internal' || kind === 'unknown',
        }),
      );
      setExpanded(true);
    }
  };

  const statusOptions = adminProspectQuickFollowUpStatusOptions(prospect.status);
  const tzLabel = adminProspectFollowUpTimeZoneLabel();

  return (
    <div className="border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <button
          type="button"
          className="text-sm font-medium text-indigo-700 underline-offset-2 hover:underline"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? t('admin.prospection.followUpWorkspace.quickActions.hide')
            : t('admin.prospection.followUpWorkspace.quickActions.show')}
        </button>
        {typeof onClose === 'function' ? (
          <button
            type="button"
            className="text-sm text-slate-600 underline-offset-2 hover:underline"
            onClick={onClose}
          >
            {t('admin.prospection.followUpWorkspace.quickActions.close')}
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div
          className="mt-3 space-y-3"
          aria-label={t('admin.prospection.followUpWorkspace.quickActions.label')}
        >
          {statusReview || followUpReview ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
              role="status"
            >
              <p className="font-medium">{t('admin.prospection.quickFollowUp.conflict.title')}</p>
              <p className="mt-1">{t('admin.prospection.quickFollowUp.conflict.message')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="text-sm font-medium text-amber-900 underline"
                  onClick={() =>
                    setFormState((current) =>
                      dismissQuickFollowUpReview(current, statusReview ? 'status' : 'nextFollowUpAt', {
                        keepDraft: true,
                      }),
                    )
                  }
                >
                  {t('admin.prospection.quickFollowUp.conflict.keepDraft')}
                </button>
                <button
                  type="button"
                  className="text-sm font-medium text-amber-900 underline"
                  onClick={() =>
                    setFormState((current) =>
                      dismissQuickFollowUpReview(current, statusReview ? 'status' : 'nextFollowUpAt', {
                        keepDraft: false,
                      }),
                    )
                  }
                >
                  {t('admin.prospection.quickFollowUp.conflict.useServer')}
                </button>
              </div>
            </div>
          ) : null}

          {formState.refreshAfterWrite?.messageKey ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
              role="alert"
              data-testid="workspace-quick-follow-up-refresh-failure"
            >
              <p>{t(formState.refreshAfterWrite.messageKey)}</p>
              <button
                type="button"
                className="mt-2 text-sm font-medium text-amber-900 underline disabled:opacity-50"
                disabled={refreshRetryBusy || anyCrmBusy}
                aria-busy={refreshRetryBusy}
                onClick={handleRefreshRetry}
              >
                {t('admin.prospection.quickFollowUp.refreshRetry')}
              </button>
            </div>
          ) : null}

          <div>
            <label htmlFor={statusId} className="block text-xs font-medium text-slate-600">
              {t('admin.prospection.quickFollowUp.status.current')}
            </label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <select
                id={statusId}
                className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                value={formState.drafts.status}
                disabled={formState.controlUi.status.saving || anyCrmBusy}
                onChange={(event) =>
                  setFormState((current) => setQuickFollowUpDraft(current, 'status', event.target.value))
                }
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {adminProspectStatusLabel(status, t)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="rounded-md bg-slate-900 px-2.5 py-1.5 text-sm text-white disabled:opacity-50"
                disabled={formState.controlUi.status.saving || anyCrmBusy || !formState.dirty.status}
                aria-busy={formState.controlUi.status.saving}
                onClick={() => saveControl('status')}
              >
                {t('admin.prospection.quickFollowUp.status.save')}
              </button>
            </div>
            {formState.controlUi.status.errorKey ? (
              <p className="mt-1 text-xs text-rose-700" role="alert">
                {t(formState.controlUi.status.errorKey)}
              </p>
            ) : null}
            {formState.controlUi.status.success ? (
              <p className="mt-1 text-xs text-emerald-700" role="status">
                {t('admin.prospection.quickFollowUp.status.success')}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor={followUpId} className="block text-xs font-medium text-slate-600">
              {t('admin.prospection.quickFollowUp.followUp.scheduled')}
              <span className="ml-1 font-normal text-slate-500">({tzLabel})</span>
            </label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input
                id={followUpId}
                type="datetime-local"
                className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                value={formState.drafts.nextFollowUpAtLocal || ''}
                disabled={formState.controlUi.nextFollowUpAt.saving || anyCrmBusy}
                onChange={(event) =>
                  setFormState((current) =>
                    setQuickFollowUpDraft(current, 'nextFollowUpAt', event.target.value),
                  )
                }
              />
              <button
                type="button"
                className="rounded-md bg-slate-900 px-2.5 py-1.5 text-sm text-white disabled:opacity-50"
                disabled={
                  formState.controlUi.nextFollowUpAt.saving ||
                  anyCrmBusy ||
                  !formState.dirty.nextFollowUpAt
                }
                aria-busy={formState.controlUi.nextFollowUpAt.saving}
                onClick={() => saveControl('nextFollowUpAt')}
              >
                {t('admin.prospection.quickFollowUp.followUp.save')}
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 disabled:opacity-50"
                disabled={formState.controlUi.nextFollowUpAt.saving || anyCrmBusy}
                onClick={handleClearFollowUp}
              >
                {t('admin.prospection.quickFollowUp.followUp.clear')}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {prospect.nextFollowUpAt
                ? formatAdminDate(prospect.nextFollowUpAt, language)
                : ADMIN_DATE_PLACEHOLDER}
            </p>
            {formState.controlUi.nextFollowUpAt.errorKey ? (
              <p className="mt-1 text-xs text-rose-700" role="alert">
                {t(formState.controlUi.nextFollowUpAt.errorKey)}
              </p>
            ) : null}
            {formState.controlUi.nextFollowUpAt.success ? (
              <p className="mt-1 text-xs text-emerald-700" role="status">
                {t('admin.prospection.quickFollowUp.followUp.success')}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor={noteId} className="block text-xs font-medium text-slate-600">
              {t('admin.prospection.quickFollowUp.note.label')}
            </label>
            <textarea
              id={noteId}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              value={formState.note.body}
              disabled={formState.note.status === 'pending' || anyCrmBusy}
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  note: { ...current.note, body: event.target.value },
                }))
              }
            />
            <button
              type="button"
              className="mt-2 rounded-md bg-slate-900 px-2.5 py-1.5 text-sm text-white disabled:opacity-50"
              disabled={
                formState.note.status === 'pending' ||
                anyCrmBusy ||
                !String(formState.note.body || '').trim()
              }
              aria-busy={formState.note.status === 'pending'}
              onClick={saveNote}
            >
              {t('admin.prospection.quickFollowUp.note.submit')}
            </button>
            {formState.note.errorKey ? (
              <p className="mt-1 text-xs text-rose-700" role="alert">
                {t(formState.note.errorKey)}
              </p>
            ) : null}
            {formState.note.status === 'success' ? (
              <p className="mt-1 text-xs text-emerald-700" role="status">
                {t('admin.prospection.quickFollowUp.note.success')}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
